import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import TipsAndUpdatesTwoToneIcon from '@mui/icons-material/TipsAndUpdatesTwoTone';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcR = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Tax-Exempt Form 990 Schedule R: Requirements & Instructions"
          description="Understand Form 990 Schedule R filing requirements for tax-exempt organizations. Learn how to report related organizations, and unrelated partnerships."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-r/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule R</span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-r/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule R at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule R
                        </strong>{' '}
                        is used to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          related organizations, certain transactions with
                          related organizations,
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          unrelated partnerships{' '}
                        </strong>{' '}
                        through which a tax-exempt organization conducts
                        significant activities.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations that answer{' '}
                        <strong style={{ fontWeight: 600 }}>"Yes"</strong> to
                        the applicable questions in{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990, Part IV (lines 33–37)
                        </strong>{' '}
                        generally must complete and attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule R</strong>{' '}
                        completing only the parts required for their reporting
                        situation.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule R reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          related tax-exempt organizations, taxable
                          subsidiaries, partnerships, trusts, disregarded
                          entities,{' '}
                        </strong>
                        , and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          reportable transactions{' '}
                        </strong>
                        with related organizations.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Related Organizations:
                        </strong>{' '}
                        For Schedule R, a related organization generally
                        includes a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          parent, subsidiary, brother/sister organization,
                          supporting or supported organization,
                        </strong>
                        or another entity that meets the IRS relationship tests.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule R?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule R?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule R Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule R filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule R?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <strong style={{ fontWeight: 600 }}>
                    Form 990 Schedule R{' '}
                  </strong>
                  serves as a supplementary document attached to{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>
                  . It provides the IRS with detailed information on:
                </Typography>
                <Box component="ul" sx={{ mb: 5 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Related Organizations:
                    </strong>{' '}
                    This includes tax-exempt organizations with shared
                    governance or control, or entities treated as disregarded
                    entities of the filing organization.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transactions with Related Organizations:
                    </strong>{' '}
                    It captures details about financial transactions (sales,
                    loans, etc.) between the filing organization and its related
                    organizations.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Unrelated Partnerships:
                    </strong>{' '}
                    For certain partnerships where the organization conducts a
                    significant portion of its activities, Schedule R requires
                    disclosure of the partnership&apos;s structure and
                    activities.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule R?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We have formulated a table that indicates which organizations
                  must complete all or a part of Schedule R and attach{' '}
                  <span style={{ fontWeight: 600 }}> Schedule R</span> to{' '}
                  <span style={{ fontWeight: 600 }}>Form 990</span>.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                      '& td': { py: 0.5 },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: '#ccc',
                          }}
                        >
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 18,
                              fontWeight: 700,
                            }}
                          >
                            Type of filer
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 18,
                              fontWeight: 700,
                            }}
                          >
                            If you answer “Yes” to…
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 18,
                              fontWeight: 700,
                            }}
                          >
                            Then you must complete…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            All organizations
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Form 990, Part IV, line 33 (regarding disregarded
                            entities)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Schedule R, Part I.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            All organizations
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Form 990, Part IV, line 34 (regarding related
                            organizations)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Schedule R, Parts II, III, IV, and V, line 1, as
                            applicable.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            All organizations
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Form 990, Part IV, line 35b (regarding payments from
                            or transactions with controlled entities)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Schedule R, Part V, line 2.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Section 501(c)(3) organization
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Form 990, Part IV, line 36 (regarding transfers to
                            exempt noncharitable related organizations)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Schedule R, Part V, line 2.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            All organizations
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Form 990, Part IV, line 37 (regarding the conduct of
                            activity through unrelated partnership)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            Schedule R, Part VI.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note</strong> - If an
                  organization isn&apos;t required to file Form 990 but chooses
                  to do so, it must file a complete return and provide all of
                  the information requested, including the required schedules.
                </Alert>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Complete your Schedule R filing requirements and e-file
                      your Form 990 with TaxZerone.
                    </strong>
                    <br />
                    It’s as simple as that!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schr`}
                  >
                    E-File Your Form 990
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="scha"
                >
                  Schedule R Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-details-img.webp" />
                  <source srcSet="/assets/images/f990-schr-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R showing organization details and basic information"
                    width={760}
                    height={98}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule R filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Identification of Disregarded Entities.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part I showing details of disregarded entities, including names, EINs, activities, locations, income, assets and control information."
                    width={760}
                    height={208}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization answered “Yes” on Form 990, Part IV, line
                  33, it must complete Part I of Schedule R.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Provide the details of each disregarded entity on separate
                  lines of Part I.
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li ': { mb: 2 } }}>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Column (a) - Name, address, and EIN (if applicable) of a
                      disregarded entity
                    </strong>
                    <br />
                    Enter the full legal name and mailing address of the
                    disregarded entity. Also enter the employer identification
                    number (EIN) of the disregarded entity, if it has one.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Column (b) - Primary activity
                    </strong>
                    <br />
                    Briefly describe the primary activity of the disregarded
                    entity.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Column (c)- Legal domicile (state or foreign country)
                    </strong>
                    <br />
                    List the U.S. state (or U.S. territory) or foreign country
                    in which the disregarded entity is organized
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Column (d) - Total income
                    </strong>
                    <br />
                    Enter the amount of the filing organization&apos;s total
                    revenue reported in Form 990, Part VIII, line 12, column
                    (A), attributable to the disregarded entity.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Column (e) - End-of-year assets
                    </strong>
                    <br />
                    Enter the amount of the organization&apos;s total assets
                    reported in Form 990, Part X, line 16, column (B),
                    attributable to the disregarded entity
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Column (f) - Direct controlling entity
                    </strong>
                    <br />
                    Enter the name of the entity that directly controls the
                    disregarded entity.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  sx={{ mb: 5, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesTwoToneIcon />}
                >
                  A disregarded entity generally must use the EIN of its sole
                  member. An exception applies to employment taxes—for wages
                  paid to employees of a disregarded entity, the disregarded
                  entity must file separate employment tax returns and use its
                  own EIN on such returns.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Identification of Related Tax-Exempt Organizations.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part II listing related tax-exempt organizations, including their names, EINs, primary activities, and relationship to the filer."
                    width={760}
                    height={245}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full legal name and mailing address of the
                  disregarded entity. Also enter the employer identification
                  number (EIN) of the disregarded entity, if it has one.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly describe the primary activity of the disregarded
                  entity.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List the U.S. state (or U.S. territory) or foreign country in
                  which the disregarded entity is organized{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the IRS code section that explains the related
                  organization’s tax status, such as{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3)
                  </Link>{' '}
                  or 501(c)(6). If it’s a government entity without a 501(c)
                  letter, leave it blank.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e){' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the organization is a 501(c)(3), list its public charity
                    classification from{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-schedule-a/"
                      sx={{ color: '#0202ff' }}
                    >
                      Schedule A
                    </Link>
                    .
                  </li>
                  <li>
                    If it’s a private foundation, write{' '}
                    <span style={{ fontWeight: 600 }}>“PF.”</span>{' '}
                  </li>
                  <li>
                    If it’s a supporting organization, include its type (Type I,
                    II, or III).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of the organization that directly controls the
                  related organization. If none, write{' '}
                  <span style={{ fontWeight: 600 }}> “N/A.”</span>{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Indicate whether your organization controls this related
                  entity under section 512(b)(13) by checking Yes or No.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Identification of Related Organizations Taxable as
                  a Partnership
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part III identifying related organizations treated as partnerships, including name, EIN, ownership percentage, and activities."
                    width={760}
                    height={256}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full legal name and mailing address of the
                  disregarded entity. Also enter the employer identification
                  number (EIN) of the disregarded entity, if it has one.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly describe the primary activity of the disregarded
                  entity.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List the U.S. state (or U.S. territory) or foreign country in
                  which the disregarded entity is organized
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d){' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1.8, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the name of the organization that directly controls
                    the partnership.
                  </li>
                  <li>
                    If no one controls it, write{' '}
                    <span style={{ fontWeight: 600 }}>“N/A.” </span>
                  </li>
                  <li>
                    If your organization controls it, enter your organization’s
                    name.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e){' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1.8, '& li': { mb: 1.2 } }}>
                  <li>State the main type of income the partnership earns.</li>
                  <li>
                    Choose whether most of its income is{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      related, unrelated, or excluded from tax.{' '}
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of income your organization’s share of the
                  partnership earned during the year, based on Schedule
                  K-1.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your organization’s share of the partnership’s total
                  assets at the end of the year, including its capital account
                  and share of liabilities.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether your share of income, losses, or
                  distributions was different from your ownership percentage at
                  any time during the year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    unrelated business income
                  </Link>{' '}
                  amount shown as Code V in box 20 of Schedule K-1. If none is
                  reported, write{' '}
                  <span style={{ fontWeight: 600 }}>“N/A.” </span>{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (j){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State whether your organization served as a general partner or
                  managing member of the partnership at any time during the
                  year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (k){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter your organization’s ownership percentage in the
                  partnership, using whichever is higher profits interest or
                  capital interest.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Identification of Related Organizations Taxable as a
                  Corporation or Trust
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part4-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part IV identifying related organizations taxed as corporations or trusts, including name, EIN, ownership interest, and entity type."
                    width={760}
                    height={246}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full legal name and mailing address of the
                  disregarded entity. Also enter the employer identification
                  number (EIN) of the disregarded entity, if it has one.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly describe the primary activity of the disregarded
                  entity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List the U.S. state (or U.S. territory) or foreign country in
                  which the disregarded entity is organized
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of the entity that directly controls the
                  disregarded entity.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Specify what kind of entity it is for tax purposes:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1.8, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> C</span> for a C
                    corporation
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>S</span> for an S
                    corporation{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>T</span> for trust.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List your organization's portion of the year's income based on
                  your ownership share or the total from Schedule K-1.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State your organization's portion of the final asset values
                  for the year, calculated using your ownership percentage.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State what percentage of the entity your organization owns at
                  year-end. This shows your actual stake in the entity.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Select Yes if your organization has control under section
                  512(b)(13) or No if it does not.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V - Transactions With Related Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part5-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part V showing transactions with related organizations, including types of transactions, amounts, and relationship details."
                    width={760}
                    height={329}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section is used to report any financial or asset
                  transactions your organization had with related organizations
                  during the tax year (other than fully disregarded entities). A
                  single transaction can appear in more than one line if it fits
                  multiple categories.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Income received from a related organization, like interest,
                  rent, royalties, or annuities.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Gifts, grants, or capital contributions your organization gave
                  to related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Gifts, grants, or capital contributions your organization
                  received from related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Loans or loan guarantees your organization made to related
                  organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1e{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Loans or loan guarantees your organization received from
                  related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1f{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Dividends received from related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1g{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Sale of assets your organization sold to related
                  organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1h{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Purchase of assets your organization bought from related
                  organizations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1i{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Exchange of assets with related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1j{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Leasing out your facilities, equipment, or other assets to
                  related organizations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1k{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Leasing facilities, equipment, or other assets from related
                  organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1l{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Services, fundraising, or membership work your organization
                  performed for related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1m{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Services, fundraising, or membership work performed by related
                  organizations for your organization.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1n{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Sharing resources like facilities, equipment, or mailing lists
                  with related organizations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1o{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Sharing paid employees with related organizations.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1p{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Reimbursing related organizations for expenses they paid on
                  your behalf.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1q{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Receiving reimbursement from related organizations for
                  expenses your organization paid.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1r{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Any other transfer of money or property your organization gave
                  to related organizations that doesn’t fit above categories.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1s{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Any other transfer of money or property your organization
                  received from related organizations that doesn’t fit above
                  categories.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part5-line2-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part5-line2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part5-line2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part V reporting transactions with related organizations, including grants, loans, asset transfers, and other financial dealings-Line2"
                    width={760}
                    height={195}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is for reporting transactions with controlled
                  entities under section 512(b)(13) or with certain related
                  tax-exempt organizations if amounts are significant.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Write the full legal name of the related organization involved
                  in the transaction. If it’s a split-interest trust (like a
                  charitable remainder or lead trust), you can simply use the
                  type of trust instead of the name and include the number of
                  trusts in parentheses if there are multiple.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the code (1a–1s) that matches the type of transaction.
                  Combine all transactions of the same type with the same
                  organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report the fair market value of what was provided or received,
                  whichever is higher. This can include cash, services, or other
                  assets.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Explain in plain terms how you calculated the value of the
                  transaction reported in column (c). This can include the
                  method used to determine the fair market value of cash,
                  services, or other assets exchanged.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VI - Unrelated Organizations Taxable as a Partnership
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part6-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part6-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part VI identifying unrelated organizations treated as partnerships, including name, EIN, ownership, and primary activities."
                    width={760}
                    height={500}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part VI is for reporting unrelated partnerships in which your
                  organization is a partner or member, and significant activity
                  is conducted through them.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the full legal name and mailing address of the
                  disregarded entity. Also enter the employer identification
                  number (EIN) of the disregarded entity, if it has one.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Briefly describe the primary activity of the disregarded
                  entity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  List the U.S. state (or U.S. territory) or foreign country in
                  which the disregarded entity is organized.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  State the main type of income the partnership earns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Choose whether most of its income is{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    related, unrelated, or excluded from tax.
                  </span>{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check Yes if all partners are{' '}
                  <Link
                    href="https://blog.taxzerone.com/posts/what-is-501c3-a-clear-guide-to-nonprofit-tax-exempt-status/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) organizations
                  </Link>{' '}
                  or government units; otherwise, check No.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the amount of income your organization’s share of the
                  partnership earned during the year, based on Schedule K-1.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter your organization’s share of the partnership’s total
                  assets at the end of the year, including its capital account
                  and share of liabilities.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Indicate whether your share of income, losses, or
                  distributions was different from your ownership percentage at
                  any time during the year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the unrelated business income amount shown as Code V in
                  box 20 of Schedule K-1. If none is reported, write{' '}
                  <span style={{ fontWeight: 600 }}> “N/A.”</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (j)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  State whether your organization served as a general partner or
                  managing member of the partnership at any time during the
                  year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (k)
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter your organization’s ownership percentage in the
                  partnership, using whichever is higher profits interest or
                  capital interest.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VII - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schr-part7-img.webp" />
                  <source srcSet="/assets/images/f990-schr-part7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schr-part7-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule R Part VII providing supplemental information and additional explanations for responses given in other parts of Schedule R."
                    width={760}
                    height={451}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Use this part if the organization needs space to provide
                  additional information in response to questions in Schedule R
                  (Form 990). In Part VII, identify the specific part and line
                  number that each response supports in the order in which those
                  parts and lines appear on Schedule R (Form 990).
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule R filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone is an IRS-authorized e-file service provider;
                  meaning you get instant updates on your 990 return filing
                  status. We ensure help is available in every step to provide
                  you with a smooth e-filing experience!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here's how your{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-return filing
                  </Link>{' '}
                  with Schedule R is securely submitted to the IRS in 3 simple
                  steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide your organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete{' '}
                    <span style={{ fontWeight: 600 }}> Schedule R</span> and
                    preview the information provided in the return for accuracy
                    before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit{' '}
                    <span style={{ fontWeight: 600 }}> Schedule R</span> and
                    your
                    <span style={{ fontWeight: 600 }}> 990 return</span> to the
                    IRS and get the acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if the IRS rejects your 990 information return due to any
                  reason, you can correct and retransmit it with TaxZerone for
                  <span style={{ fontWeight: 600 }}> FREE!</span>
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 0, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Ready to attach Schedule R along with your 990 return
                        with TaxZerone?
                      </Typography>
                      <Typography variant="body2">
                        Make the e-filing process simple and hassle-free by
                        clicking the button below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schr`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What qualifies as a related organization in Schedule R?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Related organizations include entities that have a close
                    relationship with the filing organization, such as
                    subsidiaries, affiliates, and other entities under common
                    control. This can include organizations with common board
                    members, officers, or key employees.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What types of transactions need to be reported on Schedule
                  R?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule R requires reporting on various types of
                    transactions between the filing organization and its related
                    parties, including loans, grants, leases, management fees,
                    and other financial transactions.
                    <br />
                    <br />
                    Additionally, certain non-financial transactions, such as
                    shared facilities or personnel, may also need to be
                    reported.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Are there any exceptions to the reporting requirement for
                  Schedule R?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Certain small organizations may be exempt from filing
                    Schedule R if they meet specific criteria outlined by the
                    IRS. Additionally, certain transactions below a certain
                    threshold amount may be exempt from reporting, but
                    organizations should carefully review IRS guidelines to
                    determine their reporting obligations.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcR;
